export type IndiaGateway = "payu" | null;
export type GlobalGateway = "stripe" | null;
export type LegacyGateway = "payu" | "stripe" | null;

const DEFAULT_INDIA_GATEWAY: Exclude<IndiaGateway, null> = "payu";
const DEFAULT_GLOBAL_GATEWAY: Exclude<GlobalGateway, null> = "stripe";

export interface PricingSource {
	id: number;
	name: string;
	inr: number | string;
	usd: number | string;
	features: string | string[] | null;
	paymentLink?: string | null;
	popular?: boolean | null;
	type: string;
	category?: string | null;
	tier?: string | null;
	paymentGateway?: string | null;
	stripePriceId?: string | null;
	payuProductInfo?: string | null;
	indiaGateway?: string | null;
	indiaPayuProductInfo?: string | null;
	indiaStripePriceId?: string | null;
	indiaPaymentLink?: string | null;
	globalGateway?: string | null;
	globalStripePriceId?: string | null;
	globalPaymentLink?: string | null;
}

export interface PricingRecord {
	id: number;
	name: string;
	features: string[];
	price_in_inr: number;
	price_in_usd: number;
	popular: boolean;
	tier?: string | null;
	type: string;
	billing_type: string;

	india_gateway: IndiaGateway;
	india_payu_product_info: string | null;
	india_stripe_price_id: string | null;

	global_gateway: GlobalGateway;
	global_stripe_price_id: string | null;

	payment_gateway?: LegacyGateway;
	stripe_price_id?: string | null;
	payu_product_info?: string | null;
}

export interface PricingData {
	subscriptions: PricingRecord[];
	monetization: PricingRecord[];
	views: PricingRecord[];
	editor: PricingRecord[];
	audit: PricingRecord[];
}

export interface PricingPaymentConfig {
	indiaGateway: IndiaGateway;
	globalGateway: GlobalGateway;
	indiaPayuProductInfo: string | null;
	indiaStripePriceId: string | null;
	globalStripePriceId: string | null;
	legacyGateway: LegacyGateway;
}

function convertIndianToInternational(text: string): string {
	return text
		.replace(/(\d+)\s*Lac\+?/gi, (_, num) => {
			const value = parseInt(num, 10) * 100000;
			if (value >= 1000000) {
				return `${value / 1000000}M+`;
			}
			return `${value / 1000}K+`;
		})
		.replace(/(\d+)\s*Crore\+?/gi, (_, num) => {
			const value = parseInt(num, 10) * 10000000;
			return `${value / 1000000}M+`;
		});
}

function parseFeatures(
	features: string | string[] | null | undefined,
): string[] {
	if (!features) return [];
	if (Array.isArray(features)) return features;
	return features
		.split(";")
		.map((feature) => feature.trim())
		.filter(Boolean);
}

function localizeFeatures(
	features: string | string[] | null | undefined,
	isIndian: boolean,
): string[] {
	const featuresArray = parseFeatures(features);
	if (isIndian) return featuresArray;
	return featuresArray.map(convertIndianToInternational);
}

function normalizeGatewayText(value: string | null | undefined): string | null {
	const normalized = value?.trim().toLowerCase();
	return normalized || null;
}

function normalizeLegacyGateway(
	value: string | null | undefined,
): LegacyGateway {
	const normalized = normalizeGatewayText(value);
	if (normalized === "payu" || normalized === "stripe") return normalized;
	return null;
}

function normalizeIndiaGateway(value: string | null | undefined): IndiaGateway {
	const normalized = normalizeGatewayText(value);
	if (normalized === "payu") return normalized;
	return null;
}

function normalizeGlobalGateway(
	value: string | null | undefined,
): GlobalGateway {
	const normalized = normalizeGatewayText(value);
	if (normalized === "stripe") return normalized;
	return null;
}

export function resolvePricingPaymentConfig(
	row: PricingSource,
): PricingPaymentConfig {
	const legacyGateway = normalizeLegacyGateway(row.paymentGateway);
	const indiaGateway =
		normalizeIndiaGateway(row.indiaGateway) ?? DEFAULT_INDIA_GATEWAY;
	const globalGateway =
		normalizeGlobalGateway(row.globalGateway) ?? DEFAULT_GLOBAL_GATEWAY;

	return {
		indiaGateway,
		globalGateway,
		indiaPayuProductInfo:
			row.indiaPayuProductInfo ?? row.payuProductInfo ?? row.name,
		indiaStripePriceId: row.indiaStripePriceId ?? row.stripePriceId ?? null,
		globalStripePriceId: row.globalStripePriceId ?? row.stripePriceId ?? null,
		legacyGateway,
	};
}

export function serializePricingRecord(
	row: PricingSource,
	isIndian: boolean,
): PricingRecord {
	const paymentConfig = resolvePricingPaymentConfig(row);

	return {
		id: row.id,
		name: row.name,
		features: localizeFeatures(row.features, isIndian),
		price_in_inr: Number(row.inr),
		price_in_usd: Number(row.usd),
		popular: !!row.popular,
		type: row.category ?? row.type,
		billing_type: row.type,
		tier: row.tier ?? null,

		india_gateway: paymentConfig.indiaGateway,
		india_payu_product_info: paymentConfig.indiaPayuProductInfo,
		india_stripe_price_id: paymentConfig.indiaStripePriceId,

		global_gateway: paymentConfig.globalGateway,
		global_stripe_price_id: paymentConfig.globalStripePriceId,

		payment_gateway: paymentConfig.legacyGateway,
		stripe_price_id: row.stripePriceId ?? null,
		payu_product_info: row.payuProductInfo ?? null,
	};
}

export function groupPricingRecords(records: PricingRecord[]): PricingData {
	return {
		subscriptions: records.filter((record) => record.type === "subscribers"),
		monetization: records.filter((record) => record.type === "monetization"),
		views: records.filter((record) => record.type === "views"),
		editor: records.filter((record) => record.type === "editor"),
		audit: records.filter((record) => record.type === "audit"),
	};
}
